import React, { useEffect, useRef } from 'react';

const EFFECTS_CONFIG = {
  snow: { emoji: '❄', count: 40, speed: [2, 5], size: [10, 22], drift: true, color: '#ffffff' },
  rain: { emoji: '💧', count: 50, speed: [6, 12], size: [8, 14], drift: false },
  hearts: { emoji: '❤', count: 30, speed: [0.4, 1.2], size: [12, 24], drift: true, color: '#e74c3c' },
  leaves: { emoji: '🍂', count: 25, speed: [1.5, 4], size: [14, 24], drift: true },
  stars: { emoji: '⭐', count: 30, speed: [1, 3], size: [10, 20], drift: true },
  bubbles: { emoji: '🫧', count: 25, speed: [1, 3], size: [12, 26], drift: true, direction: 'up' },
  confetti: { emoji: '🎊', count: 35, speed: [2, 5], size: [10, 18], drift: true },
  fireflies: { emoji: '✨', count: 20, speed: [0.5, 2], size: [10, 18], drift: true }
};

const VisualEffect = ({ effect }) => {
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    if (!effect || effect === 'none' || !EFFECTS_CONFIG[effect]) return;

    const config = EFFECTS_CONFIG[effect];
    const container = containerRef.current;
    if (!container) return;

    const particles = [];
    for (let i = 0; i < config.count; i++) {
      const span = document.createElement('span');
      span.textContent = config.emoji;
      span.style.cssText = `
        position: fixed;
        pointer-events: none;
        z-index: 9999;
        user-select: none;
        opacity: 0;
        transition: none;
      `;
      const size = config.size[0] + Math.random() * (config.size[1] - config.size[0]);
      span.style.fontSize = size + 'px';
      if (config.color) span.style.color = config.color;
      container.appendChild(span);

      particles.push({
        el: span,
        x: Math.random() * window.innerWidth,
        y: config.direction === 'up'
          ? window.innerHeight + Math.random() * window.innerHeight
          : -size - Math.random() * window.innerHeight,
        speed: config.speed[0] + Math.random() * (config.speed[1] - config.speed[0]),
        drift: config.drift ? (Math.random() - 0.5) * 1.5 : 0,
        opacity: 0.4 + Math.random() * 0.6,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: 0.02 + Math.random() * 0.03
      });
    }
    particlesRef.current = particles;

    const animate = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;

      for (const p of particles) {
        if (config.direction === 'up') {
          p.y -= p.speed;
          if (p.y < -30) {
            p.y = h + 20;
            p.x = Math.random() * w;
          }
        } else {
          p.y += p.speed;
          if (p.y > h + 30) {
            p.y = -20;
            p.x = Math.random() * w;
          }
        }

        if (config.drift) {
          p.wobble += p.wobbleSpeed;
          p.x += Math.sin(p.wobble) * 0.8 + p.drift * 0.3;
        }

        if (p.x < -20) p.x = w + 10;
        if (p.x > w + 20) p.x = -10;

        p.el.style.transform = `translate(${p.x}px, ${p.y}px)`;
        p.el.style.opacity = p.opacity;
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      particles.forEach(p => p.el.remove());
      particlesRef.current = [];
    };
  }, [effect]);

  if (!effect || effect === 'none' || !EFFECTS_CONFIG[effect]) return null;

  return <div ref={containerRef} style={{ position: 'fixed', top: 0, left: 0, width: 0, height: 0, pointerEvents: 'none', zIndex: 9999 }} />;
};

export default VisualEffect;
